import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Filter, Euro, Calendar, User, Building2, MoreVertical, Trash2, Eye, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import api from '../services/api';

import { exportDevis } from '../utils/export';
import { formatCurrency } from '../utils/format';

const STAGE_CONFIG = {
  lead: { label: 'Lead', color: '#6366F1', bg: 'bg-[#6366F1]/10', text: 'text-[#6366F1]' },
  qualified: { label: 'Qualifié', color: '#3B82F6', bg: 'bg-[#3B82F6]/10', text: 'text-[#3B82F6]' },
  proposal: { label: 'Proposition', color: '#FFAB00', bg: 'bg-[#FFAB00]/10', text: 'text-[#B07800]' },
  negotiation: { label: 'Négociation', color: '#F97316', bg: 'bg-[#F97316]/10', text: 'text-[#F97316]' },
  won: { label: 'Gagné', color: '#1B99E5', bg: 'bg-[#1B99E5]/10', text: 'text-[#1B99E5]' },
  lost: { label: 'Perdu', color: '#EF4444', bg: 'bg-[#EF4444]/10', text: 'text-[#EF4444]' },
};

const formatValue = (v) => formatCurrency(v);

export default function Deals() {
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [stages, setStages] = useState([]);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get('/deals'), api.get('/pipeline/stages')])
      .then(([d, s]) => { setDeals(d.data); setStages(s.data); })
      .catch(() => toast.error('Erreur chargement'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = deals.filter(d => {
    const matchSearch = !search || `${d.title} ${d.company_name || ''} ${d.contact_name || ''}`.toLowerCase().includes(search.toLowerCase());
    const matchStage = stageFilter === 'all' || d.stage === stageFilter;
    return matchSearch && matchStage;
  });

  const totalValue = filtered.reduce((sum, d) => sum + (d.value || 0), 0);

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/deals/${id}`);
      setDeals(prev => prev.filter(d => d.id !== id));
      toast.success('Devis supprimé');
    } catch { toast.error('Erreur'); }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>Devis</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            {filtered.length} devis · {formatValue(totalValue)} total
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => exportDevis(filtered)} variant="outline" className="h-9 px-3 text-sm border-gray-200 text-gray-600 hover:bg-gray-50" data-testid="export-devis-btn">
            <Download size={14} className="mr-1.5" /> Exporter CSV
          </Button>
          <Button onClick={() => navigate('/pipeline')} className="bg-[#1B99E5] hover:bg-[#0f86cc] text-white h-9 px-4 text-sm font-medium rounded-lg active:scale-95"
            style={{ transition: 'background-color 150ms ease, transform 100ms ease' }} data-testid="go-pipeline-btn">
            <Plus size={15} className="mr-1.5" /> Nouveau devis
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-0 max-w-xs">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input placeholder="Rechercher un deal..." value={search} onChange={e => setSearch(e.target.value)}
            className="pl-9 h-9 text-sm border-gray-200 bg-white dark:bg-[#1A1D23]" data-testid="deals-search" />
        </div>
        <Select value={stageFilter} onValueChange={setStageFilter}>
          <SelectTrigger className="w-44 h-9 text-sm border-gray-200 bg-white dark:bg-[#1A1D23]" data-testid="stage-filter">
            <Filter size={13} className="mr-1.5 text-gray-400" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les étapes</SelectItem>
            {stages.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Stage pills summary */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {stages.map(s => {
          const cnt = deals.filter(d => d.stage === s.id).length;
          if (!cnt) return null;
          const cfg = STAGE_CONFIG[s.id];
          return (
            <button
              key={s.id}
              onClick={() => setStageFilter(stageFilter === s.id ? 'all' : s.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${
                stageFilter === s.id ? `${cfg?.bg || ''} ${cfg?.text || ''} border-current` : 'bg-white dark:bg-[#1A1D23] border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400'
              }`}
              style={{ transition: 'all 150ms ease' }}
              data-testid={`stage-pill-${s.id}`}
            >
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
              {s.name} ({cnt})
            </button>
          );
        })}
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-[#1A1D23] rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-400">Chargement...</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-12 h-12 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-3">
              <Euro size={20} className="text-gray-400" />
            </div>
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Aucun devis trouvé</p>
            <Button onClick={() => navigate('/pipeline')} size="sm" className="mt-3 bg-[#1B99E5] hover:bg-[#0f86cc] text-white">
              <Plus size={13} className="mr-1.5" /> Créer un devis
            </Button>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Devis</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden sm:table-cell">Étape</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Valeur</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden md:table-cell">Contact</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden lg:table-cell">Clôture</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden md:table-cell">Prob.</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden xl:table-cell">Responsable</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(deal => {
                const cfg = STAGE_CONFIG[deal.stage] || { label: deal.stage, bg: 'bg-gray-100', text: 'text-gray-600' };
                return (
                  <tr key={deal.id}
                    className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50/50 dark:hover:bg-gray-800/30 h-12 cursor-pointer"
                    style={{ transition: 'background-color 100ms ease' }}
                    onClick={() => navigate(`/deals/${deal.id}`)}
                    data-testid={`deal-row-${deal.id}`}
                  >
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate max-w-48">{deal.title}</p>
                      {deal.company_name && (
                        <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5"><Building2 size={10} />{deal.company_name}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${cfg.bg} ${cfg.text}`}>{cfg.label}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">{formatValue(deal.value)}</span>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      {deal.contact_name && (
                        <span className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1.5"><User size={12}/>{deal.contact_name}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      {deal.expected_close_date && (
                        <span className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1.5"><Calendar size={12}/>{deal.expected_close_date}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center hidden md:table-cell">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400">{deal.probability}%</span>
                    </td>
                    <td className="px-4 py-3 hidden xl:table-cell">
                      {deal.owner_name && (
                        <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5"><User size={11}/>{deal.owner_name}</span>
                      )}
                    </td>
                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
                            <MoreVertical size={14} className="text-gray-400" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/deals/${deal.id}`)}>
                            <Eye size={13} className="mr-2" /> Voir le détail
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => handleDelete(deal.id, e)} className="text-red-600 focus:text-red-600">
                            <Trash2 size={13} className="mr-2" /> Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
